import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useStore } from '../context/StoreContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { format, subDays } from 'date-fns';
import { 
  CalendarIcon,
  Plus,
  Ticket,
  Loader2,
  Edit,
  Trash2,
  CheckCircle,
  Gift,
  AlertTriangle,
  FileText
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const LotteryPage = () => {
  const { isAdmin, isManager, user } = useAuth();
  const isStaff = user?.user_type === 'staff';
  const { stores, selectedStore } = useStore();
  const [selectedStoreId, setSelectedStoreId] = useState('');
  const [invoices, setInvoices] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingInvoice, setEditingInvoice] = useState(null);
  const [saving, setSaving] = useState(false);
  
  // Sales data from last 7 days
  const [salesData, setSalesData] = useState({
    lottery_sales: 0,
    scratch_card_settled: 0,
    lottery_prizes: 0,
    scratch_card_prizes: 0
  });
  const [loadingSalesData, setLoadingSalesData] = useState(false);

  // Form data for invoice
  const [formData, setFormData] = useState({
    week_ending: new Date(),
    invoice_number: '',
    // From sales data (read-only display)
    system_lottery_sales: 0,
    system_scratch_settled: 0,
    system_lottery_prizes: 0,
    system_scratch_prizes: 0,
    // From invoice (editable)
    invoice_lottery_sales: 0,
    invoice_scratch_settled: 0,
    invoice_lottery_prizes: 0,
    invoice_scratch_prizes: 0,
    invoice_commission: 0,
    invoice_final_amount: 0,
    // Variance
    variance_comments: ''
  });

  useEffect(() => {
    if (stores.length > 0) {
      const storeId = selectedStore?.id || stores[0].id;
      setSelectedStoreId(storeId);
    }
  }, [stores, selectedStore]);

  useEffect(() => {
    if (selectedStoreId) {
      fetchInvoices();
      fetchSummary();
    }
  }, [selectedStoreId]);

  const fetchInvoices = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/lottery/invoices`, {
        params: { store_id: selectedStoreId }
      });
      setInvoices(response.data);
    } catch (error) {
      toast.error('Failed to load invoices');
    } finally {
      setLoading(false);
    }
  };

  const fetchSummary = async () => {
    try {
      const response = await axios.get(`${API}/lottery/summary/${selectedStoreId}`);
      setSummary(response.data);
    } catch (error) {
      console.error('Failed to load summary');
    }
  };

  const fetchSalesData = async (weekEndingDate) => {
    if (!selectedStoreId) return;
    
    setLoadingSalesData(true);
    try {
      const endDate = format(weekEndingDate, 'yyyy-MM-dd');
      const startDate = format(subDays(weekEndingDate, 6), 'yyyy-MM-dd');
      
      const response = await axios.get(`${API}/lottery/weekly-sales`, {
        params: { 
          store_id: selectedStoreId,
          start_date: startDate,
          end_date: endDate
        }
      });
      
      const data = response.data;
      setSalesData(data);
      setFormData(prev => ({
        ...prev,
        system_lottery_sales: data.lottery_sales || 0,
        system_scratch_settled: data.scratch_card_settled || 0,
        system_lottery_prizes: data.lottery_prizes || 0,
        system_scratch_prizes: data.scratch_card_prizes || 0
      }));
    } catch (error) {
      console.error('Failed to fetch sales data:', error);
      setSalesData({
        lottery_sales: 0,
        scratch_card_settled: 0,
        lottery_prizes: 0,
        scratch_card_prizes: 0
      });
    } finally {
      setLoadingSalesData(false);
    }
  };

  const handleWeekEndingChange = (date) => {
    setFormData(prev => ({ ...prev, week_ending: date }));
    fetchSalesData(date);
  };

  const resetForm = () => {
    setEditingInvoice(null);
    setFormData({
      week_ending: new Date(),
      invoice_number: '',
      system_lottery_sales: 0,
      system_scratch_settled: 0,
      system_lottery_prizes: 0,
      system_scratch_prizes: 0,
      invoice_lottery_sales: 0,
      invoice_scratch_settled: 0,
      invoice_lottery_prizes: 0,
      invoice_scratch_prizes: 0,
      invoice_commission: 0,
      invoice_final_amount: 0,
      variance_comments: ''
    });
    setSalesData({
      lottery_sales: 0,
      scratch_card_settled: 0,
      lottery_prizes: 0,
      scratch_card_prizes: 0
    });
  };

  const openAddDialog = () => {
    resetForm();
    setDialogOpen(true);
    // Fetch sales data for current week ending
    fetchSalesData(new Date());
  };

  const openEditDialog = async (invoice) => {
    setEditingInvoice(invoice);
    setFormData({
      week_ending: new Date(invoice.week_ending),
      invoice_number: invoice.invoice_number || '',
      system_lottery_sales: invoice.system_lottery_sales || 0,
      system_scratch_settled: invoice.system_scratch_settled || 0,
      system_lottery_prizes: invoice.system_lottery_prizes || 0,
      system_scratch_prizes: invoice.system_scratch_prizes || 0,
      invoice_lottery_sales: invoice.invoice_lottery_sales || 0,
      invoice_scratch_settled: invoice.invoice_scratch_settled || 0,
      invoice_lottery_prizes: invoice.invoice_lottery_prizes || 0,
      invoice_scratch_prizes: invoice.invoice_scratch_prizes || 0,
      invoice_commission: invoice.invoice_commission || 0,
      invoice_final_amount: invoice.invoice_final_amount || 0,
      variance_comments: invoice.variance_comments || ''
    });
    setDialogOpen(true);
    
    // Recalculate system data from actual settlements (to fix old invoices with wrong data)
    const weekEnding = new Date(invoice.week_ending);
    await fetchSalesData(weekEnding);
  };

  // Calculate variance between system data and invoice
  const calculateVariance = () => {
    const systemTotal = formData.system_lottery_sales + formData.system_scratch_settled - 
                        formData.system_lottery_prizes - formData.system_scratch_prizes;
    const invoiceTotal = formData.invoice_lottery_sales + formData.invoice_scratch_settled -
                         formData.invoice_lottery_prizes - formData.invoice_scratch_prizes;
    return systemTotal - invoiceTotal;
  };

  const variance = calculateVariance();
  const requiresComments = Math.abs(variance) > 1.00;

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.invoice_number) {
      toast.error('Please enter an invoice number');
      return;
    }
    
    // Calculate variance at submit time to ensure it's current
    const currentVariance = calculateVariance();
    const needsComments = Math.abs(currentVariance) > 1.00;
    
    if (needsComments && !formData.variance_comments.trim()) {
      toast.error('Variance exceeds £1.00. Please enter comments explaining the difference.');
      return;
    }

    setSaving(true);

    try {
      const payload = {
        ...formData,
        store_id: selectedStoreId,
        week_ending: format(formData.week_ending, 'yyyy-MM-dd'),
        variance: currentVariance
      };

      if (editingInvoice) {
        await axios.put(`${API}/lottery/invoices/${editingInvoice.id}`, payload);
        toast.success('Invoice updated successfully!');
      } else {
        await axios.post(`${API}/lottery/invoices`, payload);
        toast.success('Invoice recorded successfully!');
      }

      setDialogOpen(false);
      fetchInvoices();
      fetchSummary();
      resetForm();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save invoice');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (invoiceId) => {
    if (!window.confirm('Are you sure you want to delete this invoice?')) return;
    
    try {
      await axios.delete(`${API}/lottery/invoices/${invoiceId}`);
      toast.success('Invoice deleted');
      fetchInvoices();
      fetchSummary();
    } catch (error) {
      toast.error('Failed to delete invoice');
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold" style={{ fontFamily: 'Barlow Condensed' }}>Lottery Invoices</h1>
          <p className="text-muted-foreground">National Lottery weekly invoice reconciliation</p>
        </div>

        <div className="flex gap-3 w-full sm:w-auto">
          <Select value={selectedStoreId} onValueChange={setSelectedStoreId}>
            <SelectTrigger className="w-full sm:w-[200px]" data-testid="lottery-store-select">
              <SelectValue placeholder="Select store" />
            </SelectTrigger>
            <SelectContent>
              {stores.map(store => (
                <SelectItem key={store.id} value={store.id}>{store.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          {(isAdmin || isManager || isStaff) && (
            <Button onClick={openAddDialog} data-testid="add-invoice-btn">
              <Plus className="mr-2 h-4 w-4" /> New Invoice
            </Button>
          )}
        </div>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="card-hover">
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Total Sales</p>
                  <p className="text-2xl font-bold font-mono">£{summary.total_sales?.toLocaleString() || 0}</p>
                </div>
                <div className="p-3 rounded-xl bg-primary/20">
                  <Ticket className="h-6 w-6 text-primary" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="card-hover">
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Total Commission</p>
                  <p className="text-2xl font-bold font-mono text-green-400">£{summary.total_commission?.toLocaleString() || 0}</p>
                </div>
                <div className="p-3 rounded-xl bg-green-500/20">
                  <CheckCircle className="h-6 w-6 text-green-400" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="card-hover">
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">SC Settled</p>
                  <p className="text-2xl font-bold font-mono text-purple-400">£{summary.total_scratch_card_settled?.toLocaleString() || 0}</p>
                </div>
                <div className="p-3 rounded-xl bg-purple-500/20">
                  <Gift className="h-6 w-6 text-purple-400" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="card-hover">
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Total Invoices</p>
                  <p className="text-2xl font-bold">{summary.total_settlements || 0}</p>
                </div>
                <div className="p-3 rounded-xl bg-blue-500/20">
                  <FileText className="h-6 w-6 text-blue-400" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Invoices Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex justify-center p-8">
              <Loader2 className="h-8 w-8 animate-spin" />
            </div>
          ) : invoices.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <Ticket className="h-12 w-12 mx-auto mb-3 opacity-30" />
              <p>No invoices recorded yet</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-border bg-muted/30">
                    <th className="text-left p-4 font-medium text-muted-foreground">Week Ending</th>
                    <th className="text-left p-4 font-medium text-muted-foreground">Invoice #</th>
                    <th className="text-right p-4 font-medium text-muted-foreground">Lottery Sales</th>
                    <th className="text-right p-4 font-medium text-muted-foreground">SC Settled</th>
                    <th className="text-right p-4 font-medium text-muted-foreground">Commission</th>
                    <th className="text-right p-4 font-medium text-muted-foreground">Final Amount</th>
                    <th className="text-right p-4 font-medium text-muted-foreground">Variance</th>
                    {(isAdmin || isManager || isStaff) && (
                      <th className="text-right p-4 font-medium text-muted-foreground">Actions</th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {invoices.map((invoice) => (
                    <tr key={invoice.id} className="border-b border-border/50 table-row-hover">
                      <td className="p-4 font-medium">
                        {new Date(invoice.week_ending).toLocaleDateString('en-GB')}
                      </td>
                      <td className="p-4 font-mono">{invoice.invoice_number || '-'}</td>
                      <td className="p-4 text-right font-mono">£{(invoice.invoice_lottery_sales || 0).toLocaleString()}</td>
                      <td className="p-4 text-right font-mono text-purple-400">£{(invoice.invoice_scratch_settled || 0).toLocaleString()}</td>
                      <td className="p-4 text-right font-mono text-green-400">£{(invoice.invoice_commission || 0).toLocaleString()}</td>
                      <td className="p-4 text-right font-mono font-semibold">
                        £{(invoice.invoice_final_amount || 0).toLocaleString()}
                      </td>
                      <td className={`p-4 text-right font-mono ${
                        Math.abs(invoice.variance || 0) > 1 ? 'text-red-400' : 'text-green-400'
                      }`}>
                        £{(invoice.variance || 0).toFixed(2)}
                      </td>
                      {(isAdmin || isManager || isStaff) && (
                        <td className="p-4 text-right">
                          <div className="flex justify-end gap-1">
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => openEditDialog(invoice)}
                              data-testid={`edit-invoice-${invoice.id}`}
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            {isAdmin && (
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handleDelete(invoice.id)}
                                className="text-destructive"
                                data-testid={`delete-invoice-${invoice.id}`}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Invoice Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Barlow Condensed' }}>
              {editingInvoice ? 'Edit Invoice' : 'New Invoice'}
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Week Ending & Invoice Number */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Week Ending</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className="w-full justify-start text-left font-normal"
                      data-testid="invoice-week-ending"
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {format(formData.week_ending, 'PPP')}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={formData.week_ending}
                      onSelect={(date) => date && handleWeekEndingChange(date)}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>

              <div className="space-y-2">
                <Label>Invoice Number *</Label>
                <Input
                  value={formData.invoice_number}
                  onChange={(e) => setFormData({ ...formData, invoice_number: e.target.value })}
                  placeholder="e.g., NL-2026-001"
                  className="font-mono"
                  data-testid="invoice-number"
                />
              </div>
            </div>

            {/* System Sales Data (Read-Only) */}
            <Card className="bg-muted/30">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2">
                  <Ticket className="h-4 w-4" />
                  Sales Data (Last 7 Days from System)
                  {loadingSalesData && <Loader2 className="h-4 w-4 animate-spin" />}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="p-3 bg-background rounded-lg">
                    <p className="text-xs text-muted-foreground">Lottery Sales</p>
                    <p className="text-lg font-bold font-mono">£{formData.system_lottery_sales.toFixed(2)}</p>
                  </div>
                  <div className="p-3 bg-background rounded-lg">
                    <p className="text-xs text-muted-foreground">SC Settled</p>
                    <p className="text-lg font-bold font-mono text-purple-400">£{formData.system_scratch_settled.toFixed(2)}</p>
                  </div>
                  <div className="p-3 bg-background rounded-lg">
                    <p className="text-xs text-muted-foreground">Lottery Prizes</p>
                    <p className="text-lg font-bold font-mono text-red-400">£{formData.system_lottery_prizes.toFixed(2)}</p>
                  </div>
                  <div className="p-3 bg-background rounded-lg">
                    <p className="text-xs text-muted-foreground">SC Prizes</p>
                    <p className="text-lg font-bold font-mono text-red-400">£{formData.system_scratch_prizes.toFixed(2)}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Invoice Amounts (Editable) */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2">
                  <FileText className="h-4 w-4" />
                  Invoice Amounts (From Weekly Invoice)
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label>Lottery Sales (£)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={formData.invoice_lottery_sales || ''}
                      onChange={(e) => setFormData({ ...formData, invoice_lottery_sales: parseFloat(e.target.value) || 0 })}
                      className="font-mono"
                      data-testid="invoice-lottery-sales"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Settled Scratch Card (£)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={formData.invoice_scratch_settled || ''}
                      onChange={(e) => setFormData({ ...formData, invoice_scratch_settled: parseFloat(e.target.value) || 0 })}
                      className="font-mono"
                      data-testid="invoice-scratch-settled"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Lottery Prizes (£)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={formData.invoice_lottery_prizes || ''}
                      onChange={(e) => setFormData({ ...formData, invoice_lottery_prizes: parseFloat(e.target.value) || 0 })}
                      className="font-mono"
                      data-testid="invoice-lottery-prizes"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Scratch Card Prizes (£)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={formData.invoice_scratch_prizes || ''}
                      onChange={(e) => setFormData({ ...formData, invoice_scratch_prizes: parseFloat(e.target.value) || 0 })}
                      className="font-mono"
                      data-testid="invoice-scratch-prizes"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Total Commission (£)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={formData.invoice_commission || ''}
                      onChange={(e) => setFormData({ ...formData, invoice_commission: parseFloat(e.target.value) || 0 })}
                      className="font-mono"
                      data-testid="invoice-commission"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Final Invoice Amount (£)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={formData.invoice_final_amount || ''}
                      onChange={(e) => setFormData({ ...formData, invoice_final_amount: parseFloat(e.target.value) || 0 })}
                      className="font-mono font-bold"
                      data-testid="invoice-final-amount"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Variance Warning */}
            {variance !== 0 && (
              <Card className={`${Math.abs(variance) > 1 ? 'border-red-500 bg-red-500/10' : 'border-green-500 bg-green-500/10'}`}>
                <CardContent className="p-4">
                  <div className="flex items-start gap-3">
                    {Math.abs(variance) > 1 ? (
                      <AlertTriangle className="h-5 w-5 text-red-400 flex-shrink-0 mt-0.5" />
                    ) : (
                      <CheckCircle className="h-5 w-5 text-green-400 flex-shrink-0 mt-0.5" />
                    )}
                    <div className="flex-1">
                      <p className={`font-medium ${Math.abs(variance) > 1 ? 'text-red-400' : 'text-green-400'}`}>
                        Variance: £{variance.toFixed(2)}
                      </p>
                      <p className="text-sm text-muted-foreground mt-1">
                        {Math.abs(variance) > 1 
                          ? 'Variance exceeds £1.00. Please investigate and enter comments below.'
                          : 'Variance is within acceptable range.'
                        }
                      </p>
                      
                      {requiresComments && (
                        <div className="mt-3 space-y-2">
                          <Label>Comments (Required) *</Label>
                          <Textarea
                            value={formData.variance_comments}
                            onChange={(e) => setFormData({ ...formData, variance_comments: e.target.value })}
                            placeholder="Explain the variance..."
                            rows={3}
                            data-testid="variance-comments"
                          />
                        </div>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={saving} data-testid="save-invoice-btn">
                {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {editingInvoice ? 'Update Invoice' : 'Save Invoice'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LotteryPage;
